import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  AtSymbolIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import Layout from '../components/Layout';
import ChangeEmailModal from '../components/ChangeEmailModal';
import { usersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const UserList = () => {
  const { hasPermission } = useAuth();
  const queryClient = useQueryClient();
  const [nextLinkStack, setNextLinkStack] = useState([]); // Stack of nextLinks for going back
  const [currentNextLink, setCurrentNextLink] = useState(null);
  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailChangeError, setEmailChangeError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Filtros
  const [accountEnabled, setAccountEnabled] = useState('');
  const [createdAfter, setCreatedAfter] = useState('');
  const [createdBefore, setCreatedBefore] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const limit = 20;

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', currentNextLink, searchTerm, accountEnabled, createdAfter, createdBefore, sortBy, documentType],
    queryFn: () =>
      searchTerm
        ? usersAPI.searchUsers(searchTerm)
        : usersAPI.listUsers({
            limit,
            nextLink: currentNextLink,
            accountEnabled: accountEnabled || undefined,
            createdAfter: createdAfter || undefined,
            createdBefore: createdBefore || undefined,
            sortBy: sortBy || undefined,
            documentType: documentType || undefined,
          }),
  });

  // Change email mutation
  const changeEmailMutation = useMutation({
    mutationFn: ({ userId, newEmail }) => usersAPI.changeUserEmail(userId, newEmail),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Email cambiado exitosamente');
      setShowChangeEmailModal(false);
      setSelectedUser(null);
      setEmailChangeError('');
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Error al cambiar el email';
      setEmailChangeError(errorMessage);
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: (userId) => usersAPI.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      toast.success('Usuario eliminado exitosamente');
      setShowDeleteModal(false);
      setUserToDelete(null);
    },
    onError: (error) => {
      const errorMessage = error.response?.data?.error || 'Error al eliminar el usuario';
      toast.error(errorMessage);
      setShowDeleteModal(false);
      setUserToDelete(null);
    },
  });

  const users = usersData?.data?.users || [];
  const hasMore = usersData?.data?.pagination?.hasMore || false;
  const nextLink = usersData?.data?.pagination?.nextLink || null;
  const pageNumber = nextLinkStack.length + 1;

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchTerm(search);
    setCurrentNextLink(null);
    setNextLinkStack([]);
  };

  const clearSearch = () => {
    setSearch('');
    setSearchTerm('');
    setCurrentNextLink(null);
    setNextLinkStack([]);
  };

  const clearFilters = () => {
    setAccountEnabled('');
    setCreatedAfter('');
    setCreatedBefore('');
    setSortBy('');
    setDocumentType('');
    setCurrentNextLink(null);
    setNextLinkStack([]);
  };

  const applyFilters = () => {
    setCurrentNextLink(null);
    setNextLinkStack([]);
  };

  const goToNextPage = () => {
    if (nextLink) {
      setNextLinkStack([...nextLinkStack, currentNextLink]);
      setCurrentNextLink(nextLink);
    }
  };

  const goToPreviousPage = () => {
    if (nextLinkStack.length > 0) {
      const newStack = [...nextLinkStack];
      const previousLink = newStack.pop();
      setNextLinkStack(newStack);
      setCurrentNextLink(previousLink);
    }
  };

  const handleChangeEmail = (user) => {
    setSelectedUser(user);
    setEmailChangeError('');
    setShowChangeEmailModal(true);
  };

  const handleEmailSubmit = (newEmail) => {
    if (selectedUser) {
      changeEmailMutation.mutate({ userId: selectedUser.id, newEmail });
    }
  };

  const handleDeleteUser = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteUserMutation.mutate(userToDelete.id);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Usuarios</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestionar cuentas de usuario de Azure B2C
            </p>
          </div>
          {hasPermission('user:create') && (
            <div className="mt-4 sm:mt-0">
              <Link
                to="/users/new"
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                Crear Usuario
              </Link>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="bg-white shadow rounded-lg p-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Buscar por nombre, email o documento..."
                />
              </div>
            </div>
            <button
              type="submit"
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Buscar
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={clearSearch}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Limpiar
              </button>
            )}
          </form>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-between w-full text-left"
            >
              <span className="text-sm font-medium text-gray-900">Filtros</span>
              <span className="text-sm text-gray-500">
                {showFilters ? 'Ocultar' : 'Mostrar'}
              </span>
            </button>
          </div>

          {showFilters && (
            <div className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Estado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estado
                  </label>
                  <select
                    value={accountEnabled}
                    onChange={(e) => setAccountEnabled(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </select>
                </div>

                {/* Tipo de Documento */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Documento
                  </label>
                  <select
                    value={documentType}
                    onChange={(e) => setDocumentType(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="Cédula de Ciudadanía">Cédula de Ciudadanía</option>
                    <option value="Cédula de Extranjería">Cédula de Extranjería</option>
                    <option value="Pasaporte">Pasaporte</option>
                    <option value="NIT">NIT</option>
                  </select>
                </div>

                {/* Ordenar por */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ordenar por Nombre
                  </label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  >
                    <option value="">Por defecto</option>
                    <option value="displayName_asc">A - Z</option>
                    <option value="displayName_desc">Z - A</option>
                  </select>
                </div>

                {/* Fecha Desde */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Creado Desde
                  </label>
                  <input
                    type="date"
                    value={createdAfter}
                    onChange={(e) => setCreatedAfter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>

                {/* Fecha Hasta */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Creado Hasta
                  </label>
                  <input
                    type="date"
                    value={createdBefore}
                    onChange={(e) => setCreatedBefore(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              {/* Botones de acción */}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={applyFilters}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Aplicar Filtros
                </button>
                {(accountEnabled || createdAfter || createdBefore || sortBy || documentType) && (
                  <button
                    onClick={clearFilters}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Limpiar Filtros
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Results Info */}
        {searchTerm && users.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              Se encontraron {users.length} usuario(s) que coinciden con "{searchTerm}".
              {users.length >= 100 && (
                <span className="font-semibold"> Mostrando los primeros 100 resultados. Sé más específico para obtener mejores resultados.</span>
              )}
            </p>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No se encontraron usuarios</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        ID de Usuario
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Creado
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Acciones</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <span className="text-blue-600 font-medium text-sm">
                                  {user.displayName?.charAt(0) || 'U'}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <Link
                                to={`/users/${user.id}`}
                                className="text-sm font-medium text-gray-900 hover:text-blue-600"
                              >
                                {user.displayName}
                              </Link>
                              <div className="text-sm text-gray-500">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {user.documentType || '-'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.documentNumber || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-500 font-mono">
                            {user.id}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {user.accountEnabled ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <CheckCircleIcon className="w-4 h-4 mr-1" />
                              Activo
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <XCircleIcon className="w-4 h-4 mr-1" />
                              Inactivo
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.createdDateTime).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Link
                              to={`/users/${user.id}`}
                              className="text-blue-600 hover:text-blue-900"
                              title="Ver"
                            >
                              <EyeIcon className="h-5 w-5" />
                            </Link>
                            {hasPermission('user:update') && (
                              <>
                                <button
                                  onClick={() => handleChangeEmail(user)}
                                  className="text-purple-600 hover:text-purple-900"
                                  title="Cambiar email"
                                >
                                  <AtSymbolIcon className="h-5 w-5" />
                                </button>
                                <Link
                                  to={`/users/${user.id}/edit`}
                                  className="text-green-600 hover:text-green-900"
                                  title="Editar"
                                >
                                  <PencilIcon className="h-5 w-5" />
                                </Link>
                              </>
                            )}
                            {hasPermission('user:delete') && (
                              <button
                                onClick={() => handleDeleteUser(user)}
                                className="text-red-600 hover:text-red-900"
                                title="Eliminar"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {!searchTerm && (nextLinkStack.length > 0 || hasMore) && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={goToPreviousPage}
                      disabled={nextLinkStack.length === 0}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Anterior
                    </button>
                    <button
                      onClick={goToNextPage}
                      disabled={!hasMore}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Siguiente
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Página <span className="font-medium">{pageNumber}</span>
                        {hasMore && <span> (más disponibles)</span>}
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={goToPreviousPage}
                          disabled={nextLinkStack.length === 0}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Anterior
                        </button>
                        <button
                          onClick={goToNextPage}
                          disabled={!hasMore}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Siguiente
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Change Email Modal */}
        {selectedUser && (
          <ChangeEmailModal
            isOpen={showChangeEmailModal}
            onClose={() => {
              setShowChangeEmailModal(false);
              setSelectedUser(null);
              setEmailChangeError('');
            }}
            currentEmail={selectedUser.email}
            onSubmit={handleEmailSubmit}
            isLoading={changeEmailMutation.isLoading}
            serverError={emailChangeError}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && userToDelete && (
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDeleteModal(false)}></div>

              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Eliminar Usuario
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        ¿Estás seguro de que deseas eliminar al usuario <span className="font-semibold">{userToDelete.displayName}</span> ({userToDelete.email})?
                      </p>
                      <p className="text-sm text-red-600 mt-2 font-medium">
                        Esta acción no se puede deshacer.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={deleteUserMutation.isLoading}
                    onClick={confirmDelete}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteUserMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
                  </button>
                  <button
                    type="button"
                    disabled={deleteUserMutation.isLoading}
                    onClick={() => {
                      setShowDeleteModal(false);
                      setUserToDelete(null);
                    }}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserList;
